import type { Assessment, ClassData, MarkSet } from '../types';

/**
 * Has the WEIGHTING changed since grades were released?
 *
 * `hasUnreleasedGrades` compares grades, Hub drafts and Hub offsets — everything that is a
 * per-student value. It does not compare the WEIGHTING, and the weighting decides every
 * student's average just as surely as the marks do.
 *
 * That gap became reachable the moment the assessment page grew a weight adjuster. A teacher
 * sets one quiz to 3x; every student's calculated average moves; nothing prompts a re-release;
 * and the student portal now shows per-assessment shares computed from the LIVE weights sitting
 * under a headline average snapshotted at the last release. Two numbers on one screen that
 * cannot both be true — "this is 83% of your grade so far" above a mark that was never
 * recomputed.
 *
 * ── WHY A SNAPSHOT, AND NOT "RECOMPUTE AND COMPARE" ─────────────────────────────────────────
 *
 * The obvious cheap check — recompute each average and compare it to `releasedAverages` — is
 * wrong here, and wrong in the way that is hardest to back out of. An unreleased test's grades
 * are deliberately STRIPPED from `releasedGrades`, so the released average legitimately
 * excludes them while a fresh calculation includes them. The two would never agree, the banner
 * would latch on forever, and releasing could never clear it. `hasUnreleasedGrades` carries a
 * comment about exactly that trap for the grade comparison; this is the same trap one level up.
 *
 * So the weighting is snapshotted at release and compared field by field.
 */

/** One assessment's contribution to the weighting, as snapshotted. */
export interface ReleasedWeight {
  categoryId: string;
  weight: number;
}

/** assessmentId → its weighting at the moment grades were released. */
export type ReleasedWeighting = Record<string, ReleasedWeight>;

/** `calculateAverage` reads `assessment.weight || 1`, so 0 and undefined are both 1. */
const weightOf = (a: Pick<Assessment, 'weight'>): number => (a.weight || 1);

/** Mirrors `calculateAverage`'s filter: these three flags take an assessment out entirely. */
const counts = (a: Assessment): boolean => !a.isHidden && !a.isDraft && !a.isUngraded;

/**
 * The weighting to store at release time.
 *
 * Only assessments that have at least one RELEASED grade. That bound is deliberate: a brand-new
 * assessment nobody has marked yet contributes nothing to any average, so including it would
 * make the banner fire every time a teacher created an assessment — noise that teaches people
 * to ignore the banner, which costs more than the thing it was warning about.
 */
export function weightingToRelease(
  assessments: Assessment[] | undefined,
  releasedGrades: Record<string, { assessmentId?: string }> | undefined,
): ReleasedWeighting {
  const withGrades = new Set<string>();
  for (const g of Object.values(releasedGrades || {})) {
    if (g?.assessmentId) withGrades.add(g.assessmentId);
  }
  const out: ReleasedWeighting = {};
  for (const a of assessments || []) {
    if (!withGrades.has(a.id)) continue;
    if (!counts(a)) continue;
    /**
     * Both fields coerced. `updateDoc` runs without `removeUndefined` and Firestore rejects
     * `undefined`, so one assessment stored without a categoryId key would abort the entire
     * release — releasedGrades, releasedAverages and gradesReleasedAt all unwritten, and grades
     * silently failing to reach students. The categories snapshot beside this one carries the
     * same warning for the same reason. No writer produces such an assessment today; releasing
     * must not depend on that staying true.
     */
    out[a.id] = { categoryId: String(a.categoryId ?? ''), weight: weightOf(a) };
  }
  return out;
}

/** What changed, so the prompt can say which rather than just "something". */
export type WeightingChange =
  | { kind: 'assessment-weight'; assessmentId: string; title: string; from: number; to: number }
  | { kind: 'assessment-category'; assessmentId: string; title: string }
  | { kind: 'assessment-dropped'; assessmentId: string; title: string }
  | { kind: 'category-weight'; categoryId: string; name: string; from: number; to: number };

/**
 * Every weighting change since release that would move a student's average.
 *
 * Returns an empty list — never a false positive — in the two cases where firing would latch
 * the banner permanently: a class released before this snapshot existed (no `releasedWeighting`),
 * and a class with nothing released at all.
 */
export function weightingChanges(
  classData: Pick<ClassData, 'assessments' | 'releasedGrades' | 'releasedCategories'> & {
    releasedWeighting?: ReleasedWeighting;
  },
  markSet: Pick<MarkSet, 'categories'> | null | undefined,
): WeightingChange[] {
  const snapshot = classData.releasedWeighting;
  // Nothing to compare against. A class released by an older build must not show a banner it
  // can never clear, and silence here is recoverable — the next release writes the snapshot.
  if (!snapshot || Object.keys(snapshot).length === 0) return [];

  const changes: WeightingChange[] = [];
  const byId = new Map((classData.assessments || []).map(a => [a.id, a]));

  for (const [id, was] of Object.entries(snapshot)) {
    const now = byId.get(id);
    // Deleted, hidden, turned into a draft, or made ungraded — all of which change every
    // student's average exactly as much as a weight change does.
    if (!now || !counts(now)) {
      changes.push({ kind: 'assessment-dropped', assessmentId: id, title: now?.title || 'A removed assessment' });
      continue;
    }
    if (now.categoryId !== was.categoryId) {
      changes.push({ kind: 'assessment-category', assessmentId: id, title: now.title });
      continue; // the category move already explains it; don't also report a weight delta
    }
    if (weightOf(now) !== was.weight) {
      changes.push({
        kind: 'assessment-weight', assessmentId: id, title: now.title,
        from: was.weight, to: weightOf(now),
      });
    }
  }

  /**
   * Category weights, from `releasedCategories` — already snapshotted at the same release.
   *
   * Compared by id and WEIGHT only, not with `sameMarkSetCategories`: that also compares name,
   * colour and description, none of which move a mark. Renaming Knowledge or recolouring it
   * would otherwise raise a "students have not seen these grades" prompt, which is false.
   */
  const releasedCats = classData.releasedCategories;
  if (Array.isArray(releasedCats) && releasedCats.length > 0 && markSet?.categories) {
    const wasById = new Map(releasedCats.map(c => [String(c?.id ?? ''), c?.weight ?? 0]));
    for (const cat of markSet.categories) {
      const before = wasById.get(String(cat?.id ?? ''));
      // A category that did not exist at release has no "before" — adding one only matters
      // once something in it is marked, which the assessment loop above already covers.
      if (before === undefined) continue;
      if ((cat?.weight ?? 0) !== before) {
        changes.push({
          kind: 'category-weight', categoryId: String(cat.id), name: cat.name,
          from: before, to: cat.weight ?? 0,
        });
      }
    }
  }

  return changes;
}

/** Has anything about the weighting changed since release? */
export const weightingChangedSinceRelease = (
  classData: Parameters<typeof weightingChanges>[0],
  markSet: Parameters<typeof weightingChanges>[1],
): boolean => weightingChanges(classData, markSet).length > 0;

/**
 * One line naming what changed, for the release prompt.
 *
 * Specific on purpose. "Students have not seen these grades" is the existing wording for a
 * mark change and it is wrong here — no mark changed. What changed is how much something
 * counts, and a teacher needs to know which thing so they can tell whether re-releasing is
 * what they meant.
 */
export function weightingChangeSummary(changes: WeightingChange[]): string {
  if (changes.length === 0) return '';
  const first = changes[0];
  const more = changes.length - 1;
  const tail = more > 0 ? ` (and ${more} other change${more === 1 ? '' : 's'})` : '';
  switch (first.kind) {
    case 'assessment-weight':
      return `"${first.title}" now counts ${first.to}× instead of ${first.from}×${tail}. Release again so students see the new mark.`;
    case 'assessment-category':
      return `"${first.title}" moved to a different category${tail}. Release again so students see the new mark.`;
    case 'assessment-dropped':
      return `"${first.title}" no longer counts toward the grade${tail}. Release again so students see the new mark.`;
    case 'category-weight':
      return `${first.name} is now ${first.to}% of the grade instead of ${first.from}%${tail}. Release again so students see the new mark.`;
  }
}
